import {
  DensityUom,
  DimsUom,
  HandlingUnit,
  HazmatData,
  Money,
  Product,
  TemperatureSettings,
  VolumeUom,
  WeightUom,
} from '@rygen/rygen-ui/model';

// Import directly to avoid circular dependency.
import { DEFAULT_HANDLING_UNIT } from '@/util/HandlingUnitUtil';

import { ReferenceNumber } from './ReferenceNumber';

export class LineItem {
  id?: number;
  childLineItems: LineItem[] = [];
  pickupStopSequenceNumber?: number;
  dropOffStopSequenceNumber?: number;
  productId?: number;
  productNumber?: string;
  description?: string;
  quantity?: number;
  weight?: number;
  weightUom: WeightUom = WeightUom.POUND;
  freightClass?: string;
  handlingUnit?: HandlingUnit = DEFAULT_HANDLING_UNIT;
  packagingQty?: number;
  packagingUnit?: HandlingUnit;
  specialInstructions?: string;
  referenceNumbers: ReferenceNumber[] = [];
  hazmatData: HazmatData = new HazmatData();
  temperatureSettings: TemperatureSettings = new TemperatureSettings();
  linear?: number;
  linearUom: DimsUom = DimsUom.FOOT;
  lengthDim?: number;
  widthDim?: number;
  heightDim?: number;
  dimensionalUom: DimsUom = DimsUom.INCH;
  density?: number;
  densityUom: DensityUom = DensityUom.POUND_PER_CUBIC_FOOT;
  nmfcNumber?: string;
  turnable: boolean = false;
  stackable: boolean = false;
  valueOfGoods?: Money;
  // Front-end only prop to save product before sending line item
  saveToProductList: boolean = false;

  /** Typescript discriminator */
  type?: string;

  constructor(opts?: Partial<LineItem>) {
    if (opts?.id != null) {
      this.id = opts.id;
    }
    if (opts?.childLineItems != null) {
      const Clazz = LineItem.getSubclass(this);
      this.childLineItems = opts.childLineItems.map(
        (val: LineItem) => new Clazz(val),
      );
    }
    if (opts?.pickupStopSequenceNumber != null) {
      this.pickupStopSequenceNumber = opts.pickupStopSequenceNumber;
    }
    if (opts?.dropOffStopSequenceNumber != null) {
      this.dropOffStopSequenceNumber = opts.dropOffStopSequenceNumber;
    }
    if (opts?.productId != null) {
      this.productId = opts.productId;
    }
    if (opts?.productNumber != null) {
      this.productNumber = opts.productNumber;
    }
    if (opts?.description != null) {
      this.description = opts.description;
    }
    if (opts?.quantity != null) {
      this.quantity = opts.quantity;
    }
    if (opts?.weight != null) {
      this.weight = opts.weight;
    }
    if (opts?.weightUom != null) {
      this.weightUom = opts.weightUom;
    }
    if (opts?.freightClass != null) {
      this.freightClass = opts.freightClass;
    }
    if (opts?.handlingUnit != null) {
      this.handlingUnit = opts.handlingUnit;
    }
    if (opts?.packagingQty != null) {
      this.packagingQty = opts.packagingQty;
    }
    if (opts?.packagingUnit != null) {
      this.packagingUnit = opts.packagingUnit;
    }
    if (opts?.specialInstructions != null) {
      this.specialInstructions = opts.specialInstructions;
    }
    if (opts?.referenceNumbers != null) {
      this.referenceNumbers = opts.referenceNumbers.map(
        (val) => new ReferenceNumber(val),
      );
    }
    if (opts?.hazmatData != null) {
      this.hazmatData = new HazmatData(opts.hazmatData);
    }
    if (opts?.temperatureSettings != null) {
      this.temperatureSettings = new TemperatureSettings(
        opts.temperatureSettings,
      );
    }
    if (opts?.linear != null) {
      this.linear = opts.linear;
    }
    if (opts?.linearUom != null) {
      this.linearUom = opts.linearUom;
    }
    if (opts?.lengthDim != null) {
      this.lengthDim = opts.lengthDim;
    }
    if (opts?.widthDim != null) {
      this.widthDim = opts.widthDim;
    }
    if (opts?.heightDim != null) {
      this.heightDim = opts.heightDim;
    }
    if (opts?.dimensionalUom != null) {
      this.dimensionalUom = opts.dimensionalUom;
    }
    if (opts?.density != null) {
      this.density = opts.density;
    }
    if (opts?.densityUom != null) {
      this.densityUom = opts.densityUom;
    }
    if (opts?.nmfcNumber != null) {
      this.nmfcNumber = opts.nmfcNumber;
    }
    if (opts?.turnable != null) {
      this.turnable = opts.turnable;
    }
    if (opts?.stackable != null) {
      this.stackable = opts.stackable;
    }
    if (opts?.saveToProductList != null) {
      this.saveToProductList = opts.saveToProductList;
    }
    if (opts?.type != null) {
      this.type = opts.type;
    }
    this.valueOfGoods = opts?.valueOfGoods
      ? new Money(opts.valueOfGoods)
      : undefined;
  }

  static getSubclass(lineItem: LineItem): any {
    return Object.getPrototypeOf(lineItem).constructor;
  }

  get isHazmat(): boolean {
    return this.hazmatData.hazmat === true;
  }

  get isRefrigerated(): boolean {
    return this.temperatureSettings.needsRefrigeration === true;
  }

  get volume(): number | undefined {
    if (
      this.lengthDim == null ||
      this.widthDim == null ||
      this.heightDim == null ||
      this.quantity == null
    ) {
      return undefined;
    }

    let length: number, width: number, height: number;
    const dimsUom = this.dimensionalUom;
    if ([DimsUom.INCH, DimsUom.YARD].includes(dimsUom)) {
      // Convert imperial units to FOOT
      length = DimsUom.convert(this.lengthDim, dimsUom, DimsUom.FOOT);
      width = DimsUom.convert(this.widthDim, dimsUom, DimsUom.FOOT);
      height = DimsUom.convert(this.heightDim, dimsUom, DimsUom.FOOT);
    } else if ([DimsUom.CENTIMETER, DimsUom.MILLIMETER].includes(dimsUom)) {
      // Convert SI units to METER
      length = DimsUom.convert(this.lengthDim, dimsUom, DimsUom.METER);
      width = DimsUom.convert(this.widthDim, dimsUom, DimsUom.METER);
      height = DimsUom.convert(this.heightDim, dimsUom, DimsUom.METER);
    } else {
      // Uom is already either FOOT or METER
      length = this.lengthDim;
      width = this.widthDim;
      height = this.heightDim;
    }
    return length * width * height * this.quantity;
  }

  get volumeUom(): VolumeUom {
    const dimsUom = this.dimensionalUom;
    return [DimsUom.INCH, DimsUom.FOOT, DimsUom.YARD].includes(dimsUom)
      ? VolumeUom.CUBIC_FOOT
      : VolumeUom.CUBIC_METER;
  }

  get domId(): string | undefined {
    return this.id ? `line-item-${this.id}` : undefined;
  }

  toProduct(): Product {
    return new Product({
      ...this,
      id: this.productId,
      // Name is required for create. On Update, we will grab the existing
      // product name so this doesn't get overwritten
      name: this.description,
    });
  }
}
